import { useMemo, useState } from 'react'
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks'
import { Drawer, Modal, ScrollArea, TextInput } from '@mantine/core'
import { IconZoom } from '@tabler/icons-react'
import { Typography } from '../../common/typography'
import { CompetitionTeam } from '../../performance/api'
import ComparePickerRow from './ComparePickerRow'
import classes from './ComparePicker.module.css'
import { CompareSubject } from '../../compare/Compare'
import { useCompetition } from '../../common/components/header/api'
import { useCompetitionId } from '../../common/hooks'

type FilteredTeam = CompetitionTeam & { shouldExpand: boolean }

export default function ComparePickerModal({
  opened,
  close,
  teamWithPlayers,
  subject,
  setSubject,
}: {
  opened: boolean
  close: () => void
  teamWithPlayers: CompetitionTeam[]
  subject: CompareSubject
  setSubject: React.Dispatch<React.SetStateAction<CompareSubject>>
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [searchValue, setSearchValue] = useState<string>('')
  const [debouncedSearch] = useDebouncedValue(searchValue, 200)
  const competition_id = useCompetitionId()
  const { data: competition } = useCompetition(Number(competition_id))

  const filteredTeams = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return teamWithPlayers.map((team) => ({ ...team, shouldExpand: false }))
    }

    const search = debouncedSearch.toLowerCase()

    return teamWithPlayers
      .map((team) => {
        const teamMatches = team.team_name.toLowerCase().includes(search)

        const matchedPlayers = team.players.filter((player) =>
          player.player_name.toLowerCase().includes(search)
        )

        if (teamMatches || matchedPlayers.length > 0) {
          return {
            ...team,
            players: teamMatches ? team.players : matchedPlayers,
            shouldExpand: matchedPlayers.length > 0 && !teamMatches,
          }
        }

        return null
      })
      .filter((team): team is FilteredTeam => team !== null)
  }, [teamWithPlayers, debouncedSearch])

  // Check if "Total Business" matches the search
  const showTotalBusiness = useMemo(() => {
    if (!debouncedSearch.trim()) return true
    return 'total business'.includes(debouncedSearch.toLowerCase())
  }, [debouncedSearch])

  const handleClose = () => {
    close()
    setSearchValue('')
  }

  return (
    <>
      {isDesktop ? (
        <Modal.Root
          opened={opened}
          onClose={() => handleClose()}
          centered
          classNames={{
            header: classes.modalHeader,
            body: classes.modalBody,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                <Typography fw={500} lh="125">
                  Search Player or Team
                </Typography>
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body
              data-no-match={!showTotalBusiness && filteredTeams.length === 0}
            >
              <TextInput
                classNames={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                placeholder="Search"
                rightSection={<IconZoom className={classes.zoomIcon} />}
                rightSectionPointerEvents="none"
                onChange={(e) => setSearchValue(e.currentTarget.value)}
              />
              <div className={classes.dataWrapper}>
                {showTotalBusiness || filteredTeams.length > 0 ? (
                  <ScrollArea.Autosize mah={500}>
                    {showTotalBusiness && (
                      <ComparePickerRow
                        key="overall"
                        id={0}
                        image={competition.logo.thumbnail}
                        name="Total Business"
                        type="overall"
                        subject={subject}
                        setSubject={setSubject}
                        close={close}
                      />
                    )}
                    {filteredTeams.map((team) => (
                      <ComparePickerRow
                        key={team.team_id}
                        id={team.team_id}
                        image={team.team_logo.thumbnail}
                        name={team.team_name}
                        type="team"
                        subject={subject}
                        setSubject={setSubject}
                        close={close}
                        team={team}
                        shouldExpand={team.shouldExpand}
                      />
                    ))}
                  </ScrollArea.Autosize>
                ) : (
                  <Typography
                    fs="sm"
                    fc={{ light: 'gray-6', dark: 'gray-5' }}
                    ta="center"
                    className={classes.noMatchContainer}
                  >
                    No teams or players found matching "{searchValue}"
                  </Typography>
                )}
              </div>
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={opened}
          onClose={() => handleClose()}
          position="bottom"
          classNames={{
            header: classes.drawerHeader,
            body: classes.drawerBody,
            content: classes.drawerContent,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <Drawer.Header>
              <Drawer.Title>
                <Typography fw={500} lh="125">
                  Search Player or Team
                </Typography>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body
              data-no-match={!showTotalBusiness && filteredTeams.length === 0}
            >
              <TextInput
                classNames={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                placeholder="Search"
                rightSection={<IconZoom className={classes.zoomIcon} />}
                rightSectionPointerEvents="none"
                onChange={(e) => setSearchValue(e.currentTarget.value)}
              />
              <div className={classes.dataWrapper}>
                {showTotalBusiness || filteredTeams.length > 0 ? (
                  <>
                    {showTotalBusiness && (
                      <ComparePickerRow
                        key="overall"
                        id={0}
                        image={competition.logo.thumbnail}
                        name="Total Business"
                        type="overall"
                        subject={subject}
                        setSubject={setSubject}
                        close={close}
                      />
                    )}
                    {filteredTeams.map((team) => (
                      <ComparePickerRow
                        key={team.team_id}
                        id={team.team_id}
                        image={team.team_logo.thumbnail}
                        name={team.team_name}
                        type="team"
                        subject={subject}
                        setSubject={setSubject}
                        close={close}
                        team={team}
                        shouldExpand={team.shouldExpand}
                      />
                    ))}
                  </>
                ) : (
                  <Typography
                    fc={{ light: 'gray-6', dark: 'gray-5' }}
                    ta="center"
                    className={classes.noMatchContainer}
                  >
                    No teams or players found matching "{searchValue}"
                  </Typography>
                )}
              </div>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </>
  )
}
